/**
 * Tableau de validation Manager / Admin — process + transversal (Lot 2).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  transversalAssessmentApi,
  ApiRequestError,
  type AssessmentStatus,
  type AssessmentSummaryDto,
  type TransversalAssessmentSummaryDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { StatusBadge } from "../components/ui/StatusBadge";
import { useAuth } from "../context/AuthContext";

type ValidationKind = "process" | "transversal";

export function ValidationDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [kind, setKind] = useState<ValidationKind>("process");
  const [processItems, setProcessItems] = useState<AssessmentSummaryDto[]>([]);
  const [transversalItems, setTransversalItems] = useState<TransversalAssessmentSummaryDto[]>([]);
  const [status, setStatus] = useState<AssessmentStatus | "">("submitted");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        status: status || undefined,
        year: year ? Number(year) : undefined,
      };
      if (kind === "process") {
        const res = await assessmentApi.list(filters);
        setProcessItems(res.assessments);
      } else {
        const res = await transversalAssessmentApi.list(filters);
        setTransversalItems(res.assessments);
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
      setProcessItems([]);
      setTransversalItems([]);
    } finally {
      setLoading(false);
    }
  }, [kind, status, year, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = kind === "process" ? processItems : transversalItems;

  return (
    <div className="sm-page">
      <PageHeader title={t("validation_dashboard")} subtitle={t("pending_submissions")} />

      <div className="sm-filters" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div className="sm-form-actions" style={{ margin: 0 }}>
          <button
            type="button"
            className={`sm-btn sm-btn-sm${kind === "process" ? " sm-btn-primary" : " sm-btn-secondary"}`}
            onClick={() => setKind("process")}
          >
            {t("validation_kind_process")}
          </button>
          <button
            type="button"
            className={`sm-btn sm-btn-sm${kind === "transversal" ? " sm-btn-primary" : " sm-btn-secondary"}`}
            onClick={() => setKind("transversal")}
          >
            {t("validation_kind_transversal")}
          </button>
        </div>
        <label>
          <span className="sm-muted">{t("status")}</span>
          <select
            className="sm-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as AssessmentStatus | "")}
          >
            <option value="">{t("all")}</option>
            <option value="draft">{t("status_draft")}</option>
            <option value="submitted">{t("status_submitted")}</option>
            <option value="validated">{t("status_validated")}</option>
          </select>
        </label>
        <label>
          <span className="sm-muted">{t("year")}</span>
          <input
            className="sm-input"
            style={{ width: 96 }}
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </label>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-table-wrap sm-card">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("full_name")}</th>
                {kind === "process" ? <th>{t("domain")}</th> : null}
                {kind === "process" ? <th>{t("erp")}</th> : null}
                <th>{t("year")}</th>
                <th>{t("status")}</th>
                <th>{t("submission_date")}</th>
                <th>{t("lines_scored")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={kind === "process" ? 8 : 6} className="sm-muted" style={{ textAlign: "center" }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : kind === "process" ? (
                processItems.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.userName}</strong>
                      <div className="sm-muted" style={{ fontSize: 12 }}>
                        {a.userInternalId}
                      </div>
                    </td>
                    <td>{a.domain}</td>
                    <td>{a.applicationLabel}</td>
                    <td>{a.year}</td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="sm-muted">
                      {a.submittedAt ? new Date(a.submittedAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="sm-muted">
                      {a.scoredCount}/{a.lineCount}
                    </td>
                    <td>
                      <Link to={`/assessments/${a.id}`} className="sm-btn sm-btn-secondary sm-btn-sm">
                        {user && (user.accessLevel ?? 0) >= 50 ? t("review") : t("open")}
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                transversalItems.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.userName}</strong>
                      <div className="sm-muted" style={{ fontSize: 12 }}>
                        {a.userInternalId}
                      </div>
                    </td>
                    <td>{a.year}</td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="sm-muted">
                      {a.submittedAt ? new Date(a.submittedAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="sm-muted">
                      {a.scoredCount}/{a.lineCount}
                    </td>
                    <td>
                      <Link
                        to={`/transversal-assessments/${a.id}`}
                        className="sm-btn sm-btn-secondary sm-btn-sm"
                      >
                        {t("review")}
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
